import { Link } from "react-router-dom";
import { UserCheck, Globe, CheckCircle2, Info, ArrowUpRight } from "lucide-react";
import { entityTrack } from "@/lib/entities";
import { ModuleShell, ModuleDelayed, StatusBadge, UpdatedStamp, INNER_CARD, fmtDate } from "./shell";

/* 03 — Regulation Intelligence (approved frame). Mandatory dual-scope split:
   ENTITY-SPECIFIC vs SECTOR EXPOSURE — sector context is never presented as
   entity-specific enforcement. Entity items auto-promote if they ever appear. */

const COUNTRY_CODES: Record<string, string> = {
  "united states": "US", "european union": "EU", "united kingdom": "UK",
  "japan": "JP", "singapore": "SG", "switzerland": "CH", "global": "GL",
};
const countryCode = (c?: string) =>
  COUNTRY_CODES[(c || "").toLowerCase()] || (c || "??").slice(0, 2).toUpperCase();

const stage = (s?: string | null) =>
  s ? s.replace(/[_-]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "—";

const RegItem = ({ e, scope, entityId, testid }: any) => {
  const country = e.countries?.[0];
  const badge = scope === "entity"
    ? { label: "ENTITY-SPECIFIC", color: "#1fe09c" }
    : { label: "SECTOR EXPOSURE", color: "#b79aff" };
  return (
    <div className={`${INNER_CARD} px-[14px] py-[13px] md:px-[16px] md:py-[14px]`} data-testid={testid}>
      <div className="flex items-start gap-[11px]">
        <span aria-hidden
          className="nl-mono flex items-center justify-center shrink-0 h-[28px] w-[28px] rounded-full text-[9px] font-semibold text-[#b9b9c7] bg-white/[0.04] border border-[#2b2e45]">
          {countryCode(country)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-[10px] gap-y-[6px]">
            <div className="min-w-0">
              {country && <p className="nl-geist text-[9.5px] leading-[13px] text-[#9499ad]">{country}</p>}
              <p className="nl-geist text-[13px] md:text-[13.5px] leading-[18px] font-semibold text-[#f6f7fc] mt-[2px]">{e.title}</p>
            </div>
            <StatusBadge label={badge.label} color={badge.color} />
          </div>
          {e.summary && (
            <p className="nl-geist text-[11.5px] md:text-[12px] leading-[17px] text-[#9499ad] mt-[7px] line-clamp-3">{e.summary}</p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[5px] mt-[9px]">
            <p className="nl-mono text-[8px] md:text-[8.5px] leading-[12px] font-medium uppercase text-[color:var(--nl-text-faint)]">
              Stage: {stage(e.status)} &nbsp;|&nbsp; Updated {fmtDate(e.updated_at)}
            </p>
            {e.slug && (
              <Link to={`/regulation/${e.slug}`} aria-label={`Open NewsLayer regulation detail: ${e.title}`}
                onClick={() => entityTrack("regulation_item_click", entityId, { slug: e.slug, scope })}
                className="inline-flex text-[#9499ad] hover:text-[#22d3ee] focus-visible:text-[#22d3ee] transition-colors">
                <ArrowUpRight className="h-[14px] w-[14px]" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ScopeHead = ({ icon: Icon, color, label }: any) => (
  <p className="nl-geist inline-flex items-center gap-[8px] text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase" style={{ color }}>
    <Icon aria-hidden className="h-[13px] w-[13px]" /> {label}
  </p>
);

export const EntityRegulationModule = ({ overview }: { overview: any }) => {
  const reg = overview.regulation;
  const name = overview.identity.name;
  if (!reg) return null;
  if (reg.error)
    return <ModuleDelayed num="3" title="Regulation Intelligence" sub="Entity & Sector Context" testid="entity-regulation-delayed"
      message="Regulation data is temporarily delayed. Nothing is estimated in its place." />;

  const entity = reg.entity_entries || [];
  const sector = reg.sector_entries || [];
  const entityId = overview.identity.entity_id;

  return (
    <ModuleShell num="3" title="Regulation Intelligence" sub="Entity & Sector Context" testid="entity-regulation"
      footNote={
        <span className="inline-flex items-start gap-[7px]">
          <Info aria-hidden className="h-[12px] w-[12px] shrink-0 mt-[1px] text-[#9499ad]" />
          <span data-testid="regulation-disclosure">
            Regulatory landscape is dynamic. This information is provided for general intelligence
            and is not legal advice.
          </span>
        </span>
      }
      footRight={
        <>
          <Link to="/regulation-tracker"
            className="nl-mono text-[8px] md:text-[9px] font-semibold uppercase text-[#9499ad] hover:text-[#22d3ee] transition-colors">
            Source: NewsLayer Regulation Engine →
          </Link>
          <UpdatedStamp iso={reg.fetched_at} testid="regulation-updated" />
        </>
      }>
      <p className="sr-only" data-testid="regulation-sr-summary">
        NewsLayer currently tracks {entity.length === 0 ? "no" : entity.length} {name}-specific regulatory
        action{entity.length === 1 ? "" : "s"}. {sector.length} broader regulatory development{sector.length === 1 ? " is" : "s are"} relevant
        to the sector.
      </p>

      <div className="grid lg:grid-cols-2 gap-[14px] lg:gap-[20px] items-start">
        <div className={`${INNER_CARD} px-[16px] py-[15px]`} data-testid="regulation-entity-scope">
          <ScopeHead icon={UserCheck} color="#1fe09c" label={`Entity-Specific (${name})`} />
          {entity.length ? (
            <div className="mt-[12px] space-y-[10px]">
              {entity.map((e: any, i: number) => (
                <RegItem key={e.slug || i} e={e} scope="entity" entityId={entityId} testid={`regulation-entity-item-${i}`} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center py-[26px] md:py-[38px] px-[10px]" data-testid="regulation-entity-empty">
              <CheckCircle2 aria-hidden className="h-[34px] w-[34px] text-[#1fe09c]" strokeWidth={1.4} />
              <p className="nl-sg text-[15px] md:text-[16px] leading-[22px] font-medium text-[#f6f7fc] mt-[14px] max-w-[280px]">
                No entity-specific regulatory actions currently tracked.
              </p>
              <p className="nl-geist text-[11.5px] md:text-[12px] leading-[17px] text-[#9499ad] mt-[10px] max-w-[320px]">
                NewsLayer is not aware of any regulatory actions, investigations, or enforcement
                directly naming {name} at this time.
              </p>
            </div>
          )}
        </div>

        <div className={`${INNER_CARD} px-[16px] py-[15px]`} data-testid="regulation-sector-scope">
          <ScopeHead icon={Globe} color="#22d3ee" label="Sector Exposure (DeFi / Crypto)" />
          {sector.length ? (
            <div className="mt-[12px] space-y-[10px]">
              {sector.map((e: any, i: number) => (
                <RegItem key={e.slug || i} e={e} scope="sector" entityId={entityId} testid={`regulation-sector-item-${i}`} />
              ))}
            </div>
          ) : (
            <p className="nl-geist text-[12px] leading-[17px] text-[#9499ad] mt-[12px]" data-testid="regulation-sector-empty">
              No sector-level regulatory developments currently tracked.
            </p>
          )}
        </div>
      </div>
    </ModuleShell>
  );
};
